import { IMMUTABLE_GAME_ID, IMMUTABLE_GAME_NAME } from "@/constants";
import { usePassportProvider } from "@/context";
import { useGetLinkedAccounts } from "@/hooks";
import { openAuthorizeUrl } from "@/utils/linkedaccounts/linkedAccounts";
import { onDarkBase } from "@biom3/design-tokens";
import { Body, Button, Heading, Stack } from "@biom3/react";
import type { WishlistProps } from "@ui-kit/GameSpecs";
import { useMemo } from "react";

const triumphCreatorPanelHeaderContent = (
  <>
    Compete for $10k+ rewards.
    <br />
    Post about @Triumph_Games!
  </>
);

const ravenidleCreatorPanelHeaderContent = <>RavenIdle Creator Leaderboard</>;

const overknightsCreatorPanelHeaderContent = (
  <>OverKnights Creator Leaderboard</>
);

const triumphCreatorPanelBodyContent = (
  <>
    Top 50 win 1 of 50{" "}
    <a
      href="https://tokentrove.com/collection/BattleRise"
      target="_blank"
      rel="noopener noreferrer"
    >
      BattleRise Founder’s Passes
    </a>{" "}
    valued up to $200.
    <br />
    Top 100 to win 100 Immutable Gems.
    <br />
    Top 10 contributors win additional $TRI!
    <br />
    Leaderboard points are based on views, so get creative with the{" "}
    <a
      href="https://tinyurl.com/TriumphMediaKit"
      target="_blank"
      rel="noopener noreferrer"
    >
      media kit!
    </a>
  </>
);

const ravenidleCreatorPanelBodyContent = (
  <>
    Post about @RavenIdle and compete for $10,000
    <br />
    Rank in the top 100 to compete for $10,000 in $QUEST rewards.
    <br />
    Leaderboard points are based on views, so get creative -
    <br />
    <a
      href="https://drive.google.com/drive/folders/1d4bhiThr_Bz9mn4L8HgU9bSVTvl_be6I?usp=drive_link"
      target="_blank"
      rel="noopener noreferrer"
    >
      check out these assets for inspiration
    </a>{" "}
  </>
);

const ovkCreatorPanelBodyContent = (
  <>Post about @OverKnightsGame, win $OVC allocation and USDC!</>
);

const ravenidleCreatorPanelFooterContent = (
  <>
    Post as many times as you like, from September 25 until October 16.
    <br />
    Leaderboard updates every 24 hours at midnight UTC with a 2 day delay to
    allow for growth.
    <br />
    Low quality/relevance posts will be ignored.
  </>
);

const overknightsCreatorPanelFooterContent = (
  <>
    Rank in the top 250 to secure Fragments for $OVC allocation, and in the top
    20 to win an additional share of 2,000 USDC.
    <br />
    Checkout out the{" "}
    <a
      href="https://drive.google.com/drive/u/1/folders/1c6-3CP6Jghfc0bEbhRY-Szu41Hu5noES"
      target="_blank"
      rel="noopener noreferrer"
    >
      media kit
    </a>{" "}
    to help get creative!
    <br />
    Post as many times as you like, from September 30th until October 13th.
    <br />
    Leaderboard updates every 24 hours at 1:00AM UTC.
  </>
);

export const XCreatorLeaderboardHeader = ({
  gameId,
  wishlistProps,
}: {
  gameId: string;
  wishlistProps: WishlistProps;
}) => {
  const { data: linkedAccounts } = useGetLinkedAccounts();
  const { isLoggedIn } = usePassportProvider();

  const hasLinkedXAccount = linkedAccounts?.linked_accounts.some(
    (account) => account.platform === "x",
  );

  const xUsername = useMemo(() => {
    switch (gameId) {
      case "ravenidle":
        return "@RavenIdle";
      case "overknights":
        return "@OverKnightsGame";
      case "triumph":
        return "@Triumph_Games";
      default:
        return "";
    }
  }, [gameId]);

  const headerContent = useMemo(() => {
    switch (gameId) {
      case "triumph":
        return triumphCreatorPanelHeaderContent;
      case "ravenidle":
        return ravenidleCreatorPanelHeaderContent;
      case "overknights":
        return overknightsCreatorPanelHeaderContent;
    }
  }, [gameId]);

  const bodyContent = useMemo(() => {
    switch (gameId) {
      case "ravenidle":
        return ravenidleCreatorPanelBodyContent;
      case "overknights":
        return ovkCreatorPanelBodyContent;
      case "triumph":
        return triumphCreatorPanelBodyContent;
    }
  }, [gameId]);

  const footerContent = useMemo(() => {
    switch (gameId) {
      case "overknights":
        return overknightsCreatorPanelFooterContent;
      case "ravenidle":
        return ravenidleCreatorPanelFooterContent;
      default:
        return "";
    }
  }, [gameId]);

  const handlePostToX = () => {
    if (hasLinkedXAccount) {
      // Base intent with prefilled text
      const base = `https://x.com/intent/tweet?text=${encodeURIComponent(xUsername)}`;
      // Only include a tweet to quote for RavenIdle
      const xUrl = (() => {
        if (gameId === "ravenidle") {
          const tweetToQuoteUrl =
            "https://x.com/RavenIdle/status/1970205966307336296";
          return `${base}&url=${encodeURIComponent(tweetToQuoteUrl)}`;
        }
        return base;
      })();

      // Open the link in a new tab.
      window.open(xUrl, "_blank");
    } else {
      openAuthorizeUrl(
        "link-x-account",
        "x-v2",
        "/quests",
        IMMUTABLE_GAME_NAME,
        IMMUTABLE_GAME_ID,
        true,
      );
    }
  };

  return (
    <Stack>
      <Heading
        size="medium"
        sx={{
          textAlign: "center",
          color: "base.color.text.heading.secondary",
        }}
      >
        {headerContent}
      </Heading>
      {isLoggedIn && wishlistProps.isWishlisted && (
        <>
          <Body
            size="small"
            sx={{
              textAlign: "center",
            }}
          >
            {bodyContent}
          </Body>
          <Button
            size="large"
            onClick={handlePostToX}
            sx={{
              textAlign: "center",
              w: "80%",
              alignSelf: "center",
              my: "base.spacing.x4",
              background: onDarkBase.gradient[1].spectrum,
              color: "black",
              "&::before": {
                background: onDarkBase.gradient[1].spectrum,
              },
              "&:hover": {
                background: onDarkBase.gradient[1].spectrum,
                "&::before": {
                  background: onDarkBase.gradient[1].spectrum,
                },
              },
            }}
          >
            {hasLinkedXAccount
              ? `Post about ${xUsername} on X`
              : "Link X Account to participate"}
          </Button>
          <Body
            size="small"
            sx={{
              textAlign: "center",
            }}
          >
            {footerContent}
          </Body>
        </>
      )}
    </Stack>
  );
};
